// BUILT IN
import React, { useEffect } from "react";

// DEPENDENCY
import { Button, Checkbox, Empty, Form } from "antd";
import moment from "moment";
import { useDispatch, useSelector } from "react-redux";

// SOURCE
import COMMON, {
  PLAN_TYPE,
  STATUS_CM_CHILD_TABLE,
  NODATA_TEXT,
} from "../../../../../../../common/constants/COMMON";
import { COLUMN_WIDTH_CM009 } from "../../material-cost/ui/TableMaterialCost";
import helpers from "../../../../../../../common/helpers/common";
import { isHasPrivilege } from "../../../../../../../common/helpers/privilege";
import { PRIVILEGE_PM } from "../../../../../../../common/constants/PRIVILEGE";
import { useSearchParams } from "react-router-dom";
import { EyeNote } from "../../../../../CM007/presenter/component/collapsed-table/ui/EyeNote";
import ApproveEditModal from "../../../../../../../common/components/modal-approve-edit";
import ApproveViewModal from "../../../../../../../common/components/modal-approve-view";
import { CheckBoxCollapseComponentCM007CM009 } from "../../../../../CM007/presenter/component/collapsed-table/ui/CheckBoxAll";

const NO_DATA = "データなし";
import InputFormMoneyItemComponent from "../../../../../../../common/components/common-form-item-cm/InputFormMoneyItemComponent";
import UploadEvidenceComponent from "../../../../../../../common/components/common-form-item-cm/upload-evidence";
import DateRangePickerCMComponent from "../../../../../../../common/components/common-form-item-cm/date-range-picker";
import DatePickerCMComponent from "../../../../../../../common/components/common-form-item-cm/date-picker";
import { hideImageViewerModal } from "../../../../../../../common/components/image-viewer-modal/ImageViewSlice";
import { hidePdfViewerModal } from "../../../../../../../common/components/pdf-viewer-modal/pdfViewerModalSlice";
import { RootState } from "../../../../../../../store";
import { AUTHORIZATION_CODE } from "../../../../../../../common/constants/AUTHORIZATION";
import CollapsedTableApi from "../../../../infrastructure/api/ApiCollapsedTable";
import CollapsedTableService from "../../../../usecase/ServiceCollapsedTable";
import CollapsedTableHandler from "../../../handler/HandlerCollapsedTable";

// ICON
import EyeViewDetailSvg from "../../../../../../../assets/icons/eye-view-detail.svg";
import EditButtonIconSvg from "../../../../../../../assets/icons/edit-button-icon.svg";
import DocumentIconSvg from "../../../../../../../assets/icons/document-icon.svg";
import DeleteButtonIconSvg from "../../../../../../../assets/icons/delete-button-icon.svg";
import AddRowButtonIconSvg from "../../../../../../../assets/icons/add-row-button.svg";
import EyeApprovedIcon from "../../../../../../../assets/icons/eye-approved.svg";
import EyeNotApprovedIcon from "../../../../../../../assets/icons/eye-not-approved.svg";
import { ViewDateWithRange } from "../../../../../../../common/components/view-date/ViewDate";

type props = {
  parentData: any;
  onParentCheckboxChange: (record: any, checked: boolean) => void;
};

const CollapsedTableRowsComponent = ({
  parentData,
  onParentCheckboxChange,
}: props) => {
  const dispatch = useDispatch();
  const [searchParams] = useSearchParams();

  // Initialize handler
  const api = CollapsedTableApi();
  const service = CollapsedTableService(api);
  const handler = CollapsedTableHandler(service);

  const projectId = searchParams.get("projectId");
  const constructionId = searchParams.get("constructionId");
  const checkedCollapse = useSelector(
    (state: RootState) => state.cm009.checkedCollapse
  );
  const parentStatus = useSelector(
    (state: RootState) => state.cm009.parentStatus
  );
  const dataLocal = useSelector((state: RootState) => state.cm0012.dataLocal);
  const permission = useSelector(
    (state: RootState) => state.auth.authData?.permission
  );
  const detailProject = useSelector(
    (state: RootState) => state.cm004.detailProject
  );
  const isDisabledCheckAll = handler.data.some(
    (element) =>
      element?.status?.code === STATUS_CM_CHILD_TABLE.MONEY_NOT_APPROVED.CODE
  );

  const isRoleAdmin = useSelector(
    (state: RootState) => state.auth?.authData?.isRoleAdmin
  );

  const infoContracvalidation = useSelector(
    (state: RootState) => state.cm004.infoContracvalidation
  );

  const order = useSelector(
    (state: RootState) => state.auth?.authData?.order?.code
  );
  const isOrderBasic = order === PLAN_TYPE.PLAN_BASIC;

  const checkButton = (isRoleAdmin ||
    isHasPrivilege(detailProject, [PRIVILEGE_PM]) ||
    permission?.includes(AUTHORIZATION_CODE.S48)) && isOrderBasic;

  useEffect(() => {
    handler.checkParentCBChange(parentData, checkedCollapse);
  }, [JSON.stringify(checkedCollapse)]);

  useEffect(() => {
    if (handler.checkedList.length > 0)
      onParentCheckboxChange(parentData, true);
  }, [JSON.stringify(handler.checkedList)]);

  useEffect(() => {
    return () => {
      dispatch(hideImageViewerModal());
      dispatch(hidePdfViewerModal());
    };
  }, []);

  useEffect(() => {
    handler.onDataLocalCM0012Change(dataLocal);
  }, [JSON.stringify(dataLocal)]);

  useEffect(() => {
    (async () => {
      if (parentData.id) {
        handler.resetEditingLines();
        handler.setParentId(parentData.id);
        handler.setParentData(parentData);
        await handler.getMoneyDirectCostOutsourceListLocal(parentData.child);
      }
    })();
  }, [JSON.stringify(parentData.child)]);

  const editActionButton = (record: any) => (
    <div>
      <Button
        disabled={
          String(record.id).includes("new")
            ? !handler.isEnableSaveButton(record)
            : false
        }
        type="primary"
        size="small"
        className="cm-save"
        onClick={() => handler.saveRecord(record, parentData.id)}
      >
        保存
      </Button>
      <Button
        size="small"
        className="cm-cancel"
        onClick={() => handler.cancelOperation(record)}
      >
        キャンセル
      </Button>
    </div>
  );

  const {
    MONEY_APPROVED,
    MONEY_WAITING_FOR_APPROVAL,
    MONEY_REJECT,
    MONEY_RETRIEVE,
    MONEY_NOT_APPROVED,
  } = STATUS_CM_CHILD_TABLE;

  // Calculate summary totals
  const calculateSummary = () => {
    let planOutsourceCostTotal = 0;
    let paymentCostTotal = 0;
    let actualCostTotal = 0;
    let laborCostTotal = 0;
    let reserveCostTotal = 0;
    let offsetCostTotal = 0;

    if (infoContracvalidation.projectStatusCode !== 'bot' && !infoContracvalidation.flagContractValidation) {
      return {
        planOutsourceCostTotal,
        paymentCostTotal,
        actualCostTotal,
        laborCostTotal,
        reserveCostTotal,
        offsetCostTotal,
      };
    }

    const allData = [
      ...helpers.addKeyToDataSource(handler.data),
      ...handler.newLines,
    ];

    allData.forEach(
      ({
        planOutsourceCost,
        paymentCost,
        actualCost,
        reserveCost,
        offsetCost,
        laborCost,
      }) => {
        planOutsourceCostTotal += planOutsourceCost || 0;
        paymentCostTotal += paymentCost || 0;
        actualCostTotal += actualCost || 0;
        reserveCostTotal += reserveCost || 0;
        offsetCostTotal += offsetCost || 0;
        laborCostTotal += laborCost || 0;
      }
    );

    return {
      planOutsourceCostTotal,
      paymentCostTotal,
      actualCostTotal,
      laborCostTotal,
      reserveCostTotal,
      offsetCostTotal,
    };
  };

  const shouldDisplayData = infoContracvalidation?.projectStatusCode !== 'bot' && infoContracvalidation?.flagContractValidation;
  const summary = calculateSummary();
  const dataSource = shouldDisplayData ? [
    { key: "add" },
    ...helpers.addKeyToDataSource(handler.data),
    ...handler.newLines,
  ] : []

  const rows: JSX.Element[] = [];
  // Sticky style for merged first-two-columns cells (add row & summary)
  // Using inline style to ensure it overrides CSS and works correctly
  // Width: 100px (No) + 350px (業者名/対象期間) = 450px
  const stickyMergedAddCellStyle: React.CSSProperties = {
    position: "sticky",
    left: 0,
    zIndex: 10,
    minWidth: "450px",
    maxWidth: "450px",
    width: "450px",
    background: "#ffffff",
    borderRight: "none",
    boxShadow: "2px 0 4px -2px rgba(0, 0, 0, 0.1)",
  };
  const stickyMergedSummaryCellStyle: React.CSSProperties = {
    position: "sticky",
    left: 0,
    zIndex: 10,
    minWidth: "450px",
    maxWidth: "450px",
    width: "450px",
    background: "#fafafa",
    borderRight: "none",
    boxShadow: "2px 0 4px -2px rgba(0, 0, 0, 0.1)",
    padding: "16px",
    textAlign: "left",
    fontWeight: "700",
    fontSize: "14px",
    lineHeight: "22px",
    color: "#222222",
  };

  // Header row
  rows.push(
    <tr key="child-header" className="cm009-child-header">
      <th>
        <CheckBoxCollapseComponentCM007CM009
          onCheckAllChange={handler.onCheckAllChange}
          checkAll={handler.checkAll}
          isDisabled={!isDisabledCheckAll}
          showCB={handler.data?.length > 0}
        />
      </th>
      <th>対象期間</th>
      <th>支払日</th>
      <th>支払予定金額</th>
      <th>勤怠計算</th>
      <th>請求金額</th>
      <th>エビデンス</th>
      <th>実費用</th>
      <th>保留額</th>
      <th>相殺金額</th>
      <th>差引支払金額</th>
      <th></th>
      <th></th>
    </tr>
  );

  if (!shouldDisplayData) {
    rows.push(
      <tr key="no-data-child">
        <td colSpan={8} style={{ textAlign: 'center', padding: '20px 0', background: '#ffffff' }}>
          <div style={{ position: 'sticky', left: '50%', display: 'inline-block', transform: 'translateX(-50%)' }}>
            <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description={NO_DATA} />
          </div>
        </td>
        <td colSpan={5}></td>
      </tr>
    );
  }

  // Data rows
  dataSource.forEach((record: any, index: number) => {
    const colabCompanyId = parentData?.company?.id ?? null;
    const isEditing = handler.isEditing(record);
    const isAddRow = record.key === "add";

    // Add row
    if (isAddRow) {
      rows.push(
        <tr key={record.key} className="cm009-child-add-row">
          <td colSpan={3} style={stickyMergedAddCellStyle}>
            {checkButton ? (
              <div
                className="add-row-style button-style"
                style={{ width: "fit-content" }}
                onClick={() => handler.addNewLine(parseInt(parentData.id))}
              >
                <img
                  src={AddRowButtonIconSvg}
                  alt=""
                  style={{ marginRight: 8 }}
                />
                <span>項目追加</span>
              </div>
            ) : (
              <></>
            )}
          </td>
          <td></td>
          <td></td>
          <td></td>
          <td></td>
          <td></td>
          <td></td>
          <td></td>
          <td></td>
          <td></td>
          <td></td>
        </tr>
      );
      return;
    }

    // Regular/Edit row
    rows.push(
      <tr
        key={record.key}
        className={isEditing ? "cm009-child-row cm009-child-edit-row row-no-padding" : "cm009-child-row"}
      >
        {/* Column 1: No/Checkbox */}
        <td onClick={(e) => e.stopPropagation()}>
          <span
            className="cell-cm007-style white-space-nowrap"
            style={{ width: "100%", float: "left", textAlign: "left" }}
          >
            {[
              MONEY_REJECT.CODE,
              MONEY_WAITING_FOR_APPROVAL.CODE,
              MONEY_RETRIEVE.CODE,
              MONEY_APPROVED.CODE,
            ].includes(record?.status?.code ?? "") ? (
              <Checkbox
                className="no-hover"
                disabled={true}
                style={{ marginRight: 8 }}
                checked
              />
            ) : (
              <Checkbox
                onChange={(value: any) =>
                  handler.onCheckboxChange(
                    record,
                    value?.target?.checked ? true : false
                  )
                }
                style={{ marginRight: 8 }}
                checked={handler.isCheckbox(record)}
                disabled={!record?.status}
              />
            )}
            {index}
          </span>
        </td>

        {/* Column 2: Target Period */}
        <td>
          {isEditing ? (
            <DateRangePickerCMComponent
              name="targetPeriod"
              record={record}
              onAttendanceLaborCostChange={(record, property, value) => {
                if (colabCompanyId && constructionId)
                  handler.onAttendanceLaborCostChange(
                    record,
                    property,
                    value,
                    parseInt(colabCompanyId),
                    parseInt(constructionId)
                  );
              }}
            />
          ) : (
            <span className="collapsed-table-row-style">
              <ViewDateWithRange {...record} />
            </span>
          )}
        </td>

        {/* Column 3: Payment Date */}
        <td>
          {isEditing ? (
            <DatePickerCMComponent record={record} name="paymentDate" />
          ) : (
            <span className="collapsed-table-row-style">
              {record.paymentDate
                ? moment(record.paymentDate).format(COMMON.FORMAT_DATE_OM)
                : ""}
            </span>
          )}
        </td>

        {/* Column 4: Plan Outsource Cost */}
        <td>
          {isEditing ? (
            <InputFormMoneyItemComponent
              record={record}
              name="planOutsourceCost"
              onValueNumberChange={handler.onValueNumberChange}
            />
          ) : (
            <span className="collapsed-table-row-style">
              {`${helpers.formatNumberMoney(record.planOutsourceCost)}円`}
            </span>
          )}
        </td>

        {/* Column 5: Labor Cost */}
        <td>
          <span
            className={
              isEditing
                ? "font-cell-editing"
                : "collapsed-table-row-style"
            }
          >
            {`${helpers.formatNumberMoney(record.laborCost)}円`}
          </span>
        </td>

        {/* Column 6: Payment Cost */}
        <td>
          {isEditing ? (
            <InputFormMoneyItemComponent
              record={record}
              name="paymentCost"
              onValueNumberChange={handler.onValueNumberChange}
            />
          ) : (
            <span className="collapsed-table-row-style">
              {`${helpers.formatNumberMoney(record.paymentCost)}円`}
            </span>
          )}
        </td>

        {/* Column 7: Evidence */}
        <td>
          {isEditing ? (
            <UploadEvidenceComponent
              nameForm={[`record${record.id}`, "evidence"]}
              fileNameDefault={record.evidence}
              form={handler.form}
              afterClear={handler.afterClearEvidence}
            />
          ) : record?.evidence ? (
            <div
              className="button-style"
              onClick={() => handler.handleViewEvidence(record)}
            >
              <img src={DocumentIconSvg} alt="" />
            </div>
          ) : (
            <></>
          )}
        </td>

        {/* Column 8: Actual Cost */}
        <td>
          {isEditing ? (
            <InputFormMoneyItemComponent
              record={record}
              name="actualCost"
              onValueNumberChange={handler.onValueNumberChange}
            />
          ) : (
            <span className="collapsed-table-row-style">
              {`${helpers.formatNumberMoney(record.actualCost)}円`}
            </span>
          )}
        </td>

        {/* Column 9: Reserve Cost */}
        <td>
          {isEditing ? (
            <InputFormMoneyItemComponent
              record={record}
              name="reserveCost"
              onValueNumberChange={handler.onValueNumberChange}
            />
          ) : (
            <span className="collapsed-table-row-style">
              {`${helpers.formatNumberMoney(record.reserveCost)}円`}
            </span>
          )}
        </td>

        {/* Column 10: Offset Cost */}
        <td>
          <div className="center-item">
            <div
              className="collapsed-table-row-style"
              style={{ display: "inline-block" }}
            >
              {`${helpers.formatNumberMoney(record.offsetCost)}円`}
            </div>
            <div
              style={{ marginLeft: 10 }}
              className="button-style"
              onClick={() => handler.viewDetail(record)}
            >
              <img alt="" src={EyeViewDetailSvg} />
            </div>
          </div>
        </td>

        {/* Column 11: Net Payment */}
        <td>
          <span
            className={
              isEditing
                ? "font-cell-editing"
                : "collapsed-table-row-style"
            }
          >
            {`${helpers.formatNumberMoney(
              record.actualCost - record.reserveCost - record.offsetCost
            )}円`}
          </span>
        </td>

        {/* Column 12: Status / Save-Cancel Buttons */}
        <td colSpan={isEditing ? 2 : 1}>
          {isEditing ? (
            editActionButton(record)
          ) : record?.status?.code === false ? (
            <></>
          ) : (
            <div
              className="collapsed-table-row-style center-item"
              style={{
                color: STATUS_CM_CHILD_TABLE.hasOwnProperty(
                  `${record?.status?.code?.toUpperCase()}`
                )
                  ? STATUS_CM_CHILD_TABLE[
                    `${record?.status?.code?.toUpperCase()}` as keyof typeof STATUS_CM_CHILD_TABLE
                  ].COLOR
                  : undefined,
              }}
            >
              {`${record.status.name}` || ""}
              <EyeNote
                record={record}
                viewApproveModal={handler.viewApproveModal}
              />
            </div>
          )}
        </td>

        {/* Column 13: Edit/Delete Actions */}
        {!isEditing && (
          <td>
            <div className="center-item">
              {checkButton ? (
                <>
                  {[
                    MONEY_REJECT.CODE,
                    MONEY_NOT_APPROVED.CODE,
                    MONEY_RETRIEVE.CODE,
                    MONEY_APPROVED.CODE,
                  ].includes(record?.status?.code ?? "") && (
                      <div
                        className="button-style"
                        onClick={() => handler.edit(record)}
                      >
                        <img alt="" src={EditButtonIconSvg} />
                      </div>
                    )}
                  {[
                    MONEY_REJECT.CODE,
                    MONEY_NOT_APPROVED.CODE,
                    MONEY_RETRIEVE.CODE,
                  ].includes(record?.status?.code ?? "") && (
                      <div
                        className="button-style"
                        style={{ marginLeft: 8 }}
                        onClick={() => handler.deleteRecord(record)}
                      >
                        <img alt="" src={DeleteButtonIconSvg} />
                      </div>
                    )}
                </>
              ) : (
                <></>
              )}
            </div>
          </td>
        )}
      </tr>
    );
  });

  // Summary row
  rows.push(
    <tr key="child-summary" className="cm009-child-summary children-table-summary">
      <td colSpan={3} style={stickyMergedSummaryCellStyle}>
        合計金額
      </td>

      <td>
        <span className="center-item white-space-nowrap">
          {`${helpers.formatNumberMoney(summary.planOutsourceCostTotal)}円`}
        </span>
      </td>
      <td>
        <span className="center-item white-space-nowrap">
          {`${helpers.formatNumberMoney(summary.laborCostTotal)}円`}
        </span>
      </td>
      <td>
        <span className="center-item white-space-nowrap">
          {`${helpers.formatNumberMoney(summary.paymentCostTotal)}円`}
        </span>
      </td>
      <td></td>
      <td>
        <span className="center-item white-space-nowrap">
          {`${helpers.formatNumberMoney(summary.actualCostTotal)}円`}
        </span>
      </td>
      <td>
        <span className="center-item white-space-nowrap">
          {`${helpers.formatNumberMoney(summary.reserveCostTotal)}円`}
        </span>
      </td>
      <td>
        <span className="center-item white-space-nowrap">
          {`${helpers.formatNumberMoney(summary.offsetCostTotal)}円`}
        </span>
      </td>
      <td>
        <span className="center-item white-space-nowrap">
          {`${helpers.formatNumberMoney(
            summary.actualCostTotal - summary.reserveCostTotal - summary.offsetCostTotal
          )}円`}
        </span>
      </td>
      <td></td>
      <td></td>
    </tr>
  );

  return (
    <Form
      form={handler.form}
      component={false}
      onValuesChange={(changedValues: any, values: any) => {
        handler.checkEnableButtons(changedValues, values);
      }}
    >
      {rows}
      <ApproveEditModal callbackOk={handler.updateOnNoteModal} />
      <ApproveViewModal callbackOk={handler.updateOnNoteModal} />
    </Form>
  );
};

export default CollapsedTableRowsComponent;
